import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Flex,
  List,
  Popconfirm,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';
import type { AgentClient } from '../agentClient';
import { toSafeMessage } from '../agentClient';
import {
  memoryListState,
  memoryState,
  modelLine,
  tagText,
} from '../agentsView';
import type { MemoryView } from '../agentsTabReducer';
import { agentCardLabel } from '../display';
import { canEdit, needsLoad } from '../skillEditView';
import {
  skillKey,
  unsavedSkills,
  withoutEdit,
  type SkillEdits,
  type UpdateSkillEdits,
} from '../skillEdits';
import type { AgentInfo, MemoryItem, SkillDoc, SkillHistory } from '../types';
import MimirSendSection from './MimirSendSection';
import SkillEditor from './SkillEditor';

type SkillBody = {
  status: 'loading' | 'ready' | 'error';
  /** The error message while status is 'error'. */
  text: string;
  doc?: SkillDoc;
  history?: SkillHistory;
  /** History couldn't be fetched (the doc, and so Read, still work). */
  historyError?: string;
  /** A re-fetch failed: the previous doc and history are kept. */
  refreshError?: string;
};

const AgentCard = ({
  agent,
  client,
  memory,
  onRemove,
  onRetry,
  edits,
  onEdits,
}: {
  agent: AgentInfo;
  client: AgentClient;
  memory: MemoryView | undefined;
  onRemove: (agent: string, item: MemoryItem) => void;
  onRetry: () => void;
  /** Unsaved editor text, held above the Chat/Agents switch (memory only). */
  edits: SkillEdits;
  onEdits: UpdateSkillEdits;
}) => {
  // Skills with unsaved text (kept through a Refresh or the Chat/Agents switch) reopen in the editor.
  const [reopen] = useState(() =>
    unsavedSkills(
      agent.name,
      agent.skills.filter(canEdit).map((s) => s.name),
      edits,
    ),
  );
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [editing, setEditing] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(reopen.map((name) => [name, true])),
  );
  const [bodies, setBodies] = useState<Record<string, SkillBody>>({});
  const label = agentCardLabel(agent);
  const tag = tagText(agent.tag);
  const mem = memoryState(agent);
  const listState = memoryListState(memory);

  /**
   * Fetch a skill's doc and history; also the refresh after every write. The
   * two calls are independent: Read needs only the doc. A failure keeps what
   * was fetched before and rejects, so a caller after a write can say so.
   */
  const load = async (skill: string): Promise<void> => {
    const [docR, histR] = await Promise.allSettled([
      client.getSkill(agent.name, skill),
      client.getSkillHistory(agent.name, skill),
    ]);
    const failure =
      docR.status === 'rejected'
        ? toSafeMessage(docR.reason)
        : histR.status === 'rejected'
          ? toSafeMessage(histR.reason)
          : null;
    setBodies((b) => {
      const prev = b[skill];
      if (docR.status === 'rejected') {
        return {
          ...b,
          [skill]: prev?.doc
            ? { ...prev, refreshError: failure ?? undefined }
            : { status: 'error', text: failure ?? '' },
        };
      }
      const doc = docR.value;
      return {
        ...b,
        [skill]: {
          status: 'ready',
          text: doc.body,
          doc,
          history: histR.status === 'fulfilled' ? histR.value : prev?.history,
          historyError:
            histR.status === 'rejected' ? (failure ?? undefined) : undefined,
          refreshError:
            histR.status === 'rejected' && prev?.history
              ? (failure ?? undefined)
              : undefined,
        },
      };
    });
    if (failure) throw new Error(failure);
  };

  const ensureLoaded = (skill: string) => {
    if (!needsLoad(bodies[skill])) return;
    setBodies((b) => ({ ...b, [skill]: { status: 'loading', text: '' } }));
    load(skill).catch(() => undefined);
  };

  // Load the reopened editors once, on mount (`reopen` never changes).
  useEffect(() => {
    reopen.forEach((skill) => {
      setBodies((b) =>
        b[skill] ? b : { ...b, [skill]: { status: 'loading', text: '' } },
      );
      load(skill).catch(() => undefined);
    });
  }, [reopen]);

  const toggle = (skill: string) => {
    const next = !open[skill];
    setOpen((o) => ({ ...o, [skill]: next }));
    if (next) ensureLoaded(skill);
  };

  const toggleEdit = (skill: string) => {
    const next = !editing[skill];
    setEditing((e) => ({ ...e, [skill]: next }));
    if (next) ensureLoaded(skill);
  };

  const unsaved = (skill: string) =>
    Object.prototype.hasOwnProperty.call(edits, skillKey(agent.name, skill));

  return (
    <Card
      size="small"
      style={{ marginBottom: 12 }}
      title={
        <Flex align="center" gap={6} wrap>
          <span>{label}</span>
          {tag && <Tag>{tag}</Tag>}
          <Tag color={agent.kind === 'llm' ? 'blue' : 'default'}>
            {agent.kind === 'llm' ? 'LLM' : 'Code'}
          </Tag>
        </Flex>
      }
    >
      <Flex vertical gap={10}>
        {agent.role && (
          <Typography.Text type="secondary">{agent.role}</Typography.Text>
        )}
        <div>
          <Typography.Text strong>Model: </Typography.Text>
          <Typography.Text>{modelLine(agent.model)}</Typography.Text>
          {agent.instructionsHash && (
            <div>
              <Typography.Text type="secondary">Instructions: </Typography.Text>
              <Typography.Text code>{agent.instructionsHash}</Typography.Text>
            </div>
          )}
        </div>

        <div>
          <Typography.Text strong>Skills</Typography.Text>
          {agent.skills.length === 0 ? (
            <div>
              <Typography.Text type="secondary">None.</Typography.Text>
            </div>
          ) : (
            agent.skills.map((skill) => (
              <div key={skill.name} style={{ marginTop: 4 }}>
                <Flex justify="space-between" align="baseline" gap={8}>
                  <span>
                    <Typography.Text>{skill.name}</Typography.Text>
                    {unsaved(skill.name) && (
                      <Tag color="warning" style={{ marginLeft: 6 }}>
                        Unsaved changes
                      </Tag>
                    )}
                    <Typography.Text type="secondary">
                      {' '}
                      {skill.description}
                    </Typography.Text>
                  </span>
                  <span style={{ whiteSpace: 'nowrap' }}>
                    <Button
                      size="small"
                      type="link"
                      aria-expanded={!!open[skill.name]}
                      aria-label={`${open[skill.name] ? 'Hide' : 'Read'} ${skill.name} skill`}
                      onClick={() => toggle(skill.name)}
                    >
                      {open[skill.name] ? 'Hide' : 'Read'}
                    </Button>
                    {canEdit(skill) &&
                      (editing[skill.name] && unsaved(skill.name) ? (
                        // Closing keeps unsaved text (until the page is left) unless the person discards it.
                        <Popconfirm
                          title="Keep your unsaved changes?"
                          description="They stay here until you leave the page."
                          okText="Keep"
                          cancelText="Discard changes"
                          cancelButtonProps={{ danger: true }}
                          onConfirm={() => toggleEdit(skill.name)}
                          onCancel={() => {
                            onEdits((e) =>
                              withoutEdit(e, skillKey(agent.name, skill.name)),
                            );
                            toggleEdit(skill.name);
                          }}
                        >
                          <Button
                            size="small"
                            type="link"
                            aria-expanded
                            aria-label={`Close editor for ${skill.name} skill`}
                          >
                            Close
                          </Button>
                        </Popconfirm>
                      ) : (
                        <Button
                          size="small"
                          type="link"
                          aria-expanded={!!editing[skill.name]}
                          aria-label={`${editing[skill.name] ? 'Close editor for' : 'Edit'} ${skill.name} skill`}
                          onClick={() => toggleEdit(skill.name)}
                        >
                          {editing[skill.name] ? 'Close' : 'Edit'}
                        </Button>
                      ))}
                  </span>
                </Flex>
                {editing[skill.name] && bodies[skill.name]?.doc && (
                  <SkillEditor
                    agent={agent.name}
                    skill={skill.name}
                    client={client}
                    doc={bodies[skill.name].doc as SkillDoc}
                    history={bodies[skill.name].history ?? null}
                    notice={
                      bodies[skill.name].refreshError ??
                      bodies[skill.name].historyError ??
                      null
                    }
                    onChanged={() => load(skill.name)}
                    edits={edits}
                    onEdits={onEdits}
                  />
                )}
                {editing[skill.name] &&
                  bodies[skill.name]?.status === 'loading' && (
                    <Typography.Text type="secondary">Loading…</Typography.Text>
                  )}
                {editing[skill.name] &&
                  bodies[skill.name]?.status === 'error' && (
                    <Alert
                      type="error"
                      showIcon
                      message={bodies[skill.name].text}
                    />
                  )}
                {open[skill.name] && (
                  <div style={{ paddingLeft: 8 }}>
                    {bodies[skill.name]?.status === 'ready' && (
                      <ReactMarkdown
                        rehypePlugins={[rehypeSanitize]}
                        disallowedElements={['img']}
                      >
                        {bodies[skill.name].text}
                      </ReactMarkdown>
                    )}
                    {bodies[skill.name]?.status === 'loading' && (
                      <Typography.Text type="secondary">
                        Loading…
                      </Typography.Text>
                    )}
                    {bodies[skill.name]?.status === 'error' && (
                      <Alert
                        type="error"
                        showIcon
                        message={bodies[skill.name].text}
                      />
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>

        {agent.canSend && <MimirSendSection client={client} />}

        <div>
          <Typography.Text strong>Tools</Typography.Text>
          <div style={{ marginTop: 4 }}>
            {agent.tools.length === 0 && (
              <Typography.Text type="secondary">None.</Typography.Text>
            )}
            {agent.tools.map((tool) => (
              <Tooltip key={tool.name} title={tool.name}>
                <Tag>{tool.label}</Tag>
              </Tooltip>
            ))}
          </div>
        </div>

        <div>
          <Typography.Text strong>Memory</Typography.Text>
          {mem.kind !== 'preferences' ? (
            <div>
              <Typography.Text type="secondary">{mem.text}</Typography.Text>
            </div>
          ) : (
            <div>
              <div>
                <Typography.Text type="secondary">{mem.text}</Typography.Text>
              </div>
              {listState === 'loading' && (
                <Typography.Text type="secondary">Loading…</Typography.Text>
              )}
              {listState === 'error' && (
                <Alert
                  style={{ marginTop: 4 }}
                  type="error"
                  showIcon
                  message={memory?.error}
                  action={
                    <Button size="small" onClick={onRetry}>
                      Retry
                    </Button>
                  }
                />
              )}
              {listState === 'list' && memory && (
                <>
                  {memory.error && (
                    <Alert
                      style={{ marginTop: 4 }}
                      type="error"
                      showIcon
                      message={memory.error}
                    />
                  )}
                  <List
                    size="small"
                    header="What it remembers about you"
                    dataSource={memory.items}
                    locale={{
                      emptyText:
                        'Nothing saved yet. Try “@ca keep each note point under 12 words” in Chat.',
                    }}
                    renderItem={(item) => (
                      <List.Item
                        actions={[
                          <Popconfirm
                            key="delete"
                            title="Delete this preference?"
                            okText="Delete"
                            onConfirm={() => onRemove(agent.name, item)}
                          >
                            <Button
                              size="small"
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              aria-label={`Delete preference: ${item.text}`}
                            />
                          </Popconfirm>,
                        ]}
                      >
                        {item.text}
                      </List.Item>
                    )}
                  />
                </>
              )}
            </div>
          )}
        </div>
      </Flex>
    </Card>
  );
};

export default AgentCard;
